import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box, Paper, Grid, Typography, Stack, MenuItem, Select, FormControl,
  Chip, TextField, Button, useTheme, useMediaQuery, Avatar,
  InputAdornment, Drawer, Fab, Badge, Tabs, Tab, Checkbox, CircularProgress, IconButton,
} from "@mui/material";
import {
  DndContext, PointerSensor, TouchSensor, useSensor, useSensors,
  useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import QuizIcon from "@mui/icons-material/Quiz";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ImageIcon from "@mui/icons-material/Image";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import FolderIcon from "@mui/icons-material/Folder";
import EventIcon from "@mui/icons-material/Event";
import DeadlineManagementTab from "./DeadlineManagementTab";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  PURPLE,
  PURPLE_DARK,
  PURPLE_SOFT,
  courseNameOf,
  FilterChip,
  CourseHeader,
  GroupsSidebar,
  StepBadge,
  useAcademicCoursePlans,
} from "./ResourceAllocationShared";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import api from "../../api";

const testIdOf = (t) => t?.id || t?.test_id || t?.ID;
const testNameOf = (t) => t?.test_name || t?.name || `Test #${testIdOf(t)}`;

function fileIcon(mimeType) {
  if (!mimeType) return <AttachFileIcon sx={{ fontSize: 18 }} />;
  if (mimeType.startsWith("video/")) return <VideoLibraryIcon sx={{ fontSize: 18, color: "#2196f3" }} />;
  if (mimeType === "application/pdf") return <PictureAsPdfIcon sx={{ fontSize: 18, color: "#f44336" }} />;
  if (mimeType.startsWith("image/")) return <ImageIcon sx={{ fontSize: 18, color: "#4caf50" }} />;
  return <AttachFileIcon sx={{ fontSize: 18 }} />;
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

const SUBJECT_ACCENT = {
  Maths: { color: "#1d4ed8", bg: "#dbeafe" },
  Mathematics: { color: "#1d4ed8", bg: "#dbeafe" },
  English: { color: "#7c3aed", bg: "#ede9fe" },
  "Verbal Reasoning": { color: "#0f766e", bg: "#ccfbf1" },
  "Non-Verbal Reasoning": { color: "#b45309", bg: "#fef3c7" },
};

function subjectAccent(name) {
  return SUBJECT_ACCENT[name] || { color: "#475569", bg: "#f1f5f9" };
}

function groupChipsBySubject(chips, subjectOrder = []) {
  const groups = new Map();
  chips.forEach((chip) => {
    const key = chip.subjectName?.trim() || "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(chip);
  });
  const ordered = [];
  subjectOrder.forEach((name) => {
    if (groups.has(name)) {
      ordered.push({ subjectName: name, chips: groups.get(name) });
      groups.delete(name);
    }
  });
  [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([subjectName, groupChips]) => ordered.push({ subjectName, chips: groupChips }));
  return ordered;
}

const modeChip = (mode) =>
  mode === "online"
    ? { label: "Online", bg: "#ede9fe", color: PURPLE }
    : { label: "In Person", bg: "#fef3c7", color: "#b45309" };

const statusChipStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return { label: "Active", bg: "#dcfce7", color: "#15803d" };
  if (s === "upcoming") return { label: "Upcoming", bg: "#dbeafe", color: "#1d4ed8" };
  if (s === "completed") return { label: "Completed", bg: "#e2e8f0", color: "#475569" };
  return { label: status || "—", bg: "#e2e8f0", color: "#475569" };
};

// ─── Shared week dropzone ───────────────────────────────────────────────────────

function WeekDropzone({ week, chips, courseSubjects = [], onChipDelete, selectedCount = 0, onAssignSelected }) {
  const { setNodeRef, isOver } = useDroppable({ id: `week-${week.id}` });
  const subjectGroups = useMemo(() => groupChipsBySubject(chips, courseSubjects), [chips, courseSubjects]);

  return (
    <Box ref={setNodeRef} sx={{
      p: 1.25, mb: 1, borderRadius: 1.5, border: "1px dashed",
      borderColor: isOver ? PURPLE : "#cbd5e1",
      bgcolor: isOver ? PURPLE_SOFT : "#fff",
      transition: "all 120ms ease",
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={chips.length ? 0.5 : 0}>
        <Typography variant="body2" fontWeight={700}>Week {week.week_number}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {selectedCount > 0 && onAssignSelected && (
            <Button size="small" onClick={() => onAssignSelected(week.id)}
              sx={{ minWidth: 0, py: 0, px: 0.75, fontSize: 11, textTransform: "none", fontWeight: 600, color: PURPLE }}>
              + {selectedCount} selected
            </Button>
          )}
          <Typography variant="caption" sx={{ color: chips.length ? "#15803d" : "#374151", fontWeight: 700 }}>
            {chips.length === 0 ? "Not Allocated" : `${chips.length} Allocated`}
          </Typography>
        </Stack>
      </Stack>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} display="block" mb={chips.length ? 0.75 : 0}>
        {week.start_date} – {week.end_date}
      </Typography>
      {subjectGroups.map(({ subjectName, chips: groupChips }) => {
        const accent = subjectAccent(subjectName);
        return (
          <Box key={subjectName} sx={{ mb: 0.75 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.35 }}>
              <Box sx={{ width: 3, height: 14, borderRadius: 1, bgcolor: accent.color }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: accent.color, lineHeight: 1.2 }}>
                {subjectName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>({groupChips.length})</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pl: 0.5 }}>
              {groupChips.map((c) => (
                <Chip key={c.id} size="small" label={c.label}
                  onDelete={onChipDelete ? () => onChipDelete(c) : undefined}
                  sx={{ bgcolor: accent.bg, color: accent.color, fontWeight: 600, height: 24, maxWidth: "100%",
                    "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Weeks panel (reusable, with week number filter) ───────────────────────────

function WeeksPanel({ weeks, chipsForWeek, courseSubjects = [], onChipDelete, totalAllocated, remaining, viewCourseHref, extraFooter, selectedCount = 0, onAssignSelected }) {
  const [weekFilter, setWeekFilter] = useState(null);
  const scrollRef = useRef(null);
  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 120, behavior: "smooth" });

  const filteredWeeks = weekFilter != null
    ? weeks.filter((w) => w.week_number === weekFilter)
    : weeks;

  return (
    <Paper elevation={0} sx={{
      p: 2, borderRadius: 3, border: "1px solid #eef2f7", bgcolor: "#fff",
      position: { md: "sticky" }, top: { md: 16 },
      maxHeight: { md: "calc(100vh - 32px)" },
      display: "flex", flexDirection: "column",
    }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <ViewWeekIcon sx={{ color: PURPLE, fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          Allocation Plan ({weeks.length} Weeks)
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${totalAllocated} Allocated`} sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600, height: 22 }} />
        <Chip size="small" label={`${remaining} Remaining`} sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600, height: 22 }} />
      </Stack>

      {weeks.length > 1 && (
        weeks.length > 8 ? (
          <Stack direction="row" alignItems="center" spacing={0.25} mb={1}>
            <IconButton size="small" onClick={() => scrollBy(-1)}
              sx={{ flexShrink: 0, color: "#64748b", "&:hover": { color: PURPLE }, p: 0.25 }}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box ref={scrollRef} sx={{
              flex: 1, display: "flex", flexDirection: "row", gap: 0.5,
              overflowX: "auto", flexWrap: "nowrap",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}>
              <FilterChip label="All" active={weekFilter === null} onClick={() => setWeekFilter(null)} />
              {weeks.map((w) => (
                <FilterChip
                  key={w.id}
                  label={`W${w.week_number}`}
                  active={weekFilter === w.week_number}
                  onClick={() => setWeekFilter(weekFilter === w.week_number ? null : w.week_number)}
                />
              ))}
            </Box>
            <IconButton size="small" onClick={() => scrollBy(1)}
              sx={{ flexShrink: 0, color: "#64748b", "&:hover": { color: PURPLE }, p: 0.25 }}>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
            <FilterChip label="All" active={weekFilter === null} onClick={() => setWeekFilter(null)} />
            {weeks.map((w) => (
              <FilterChip
                key={w.id}
                label={`W${w.week_number}`}
                active={weekFilter === w.week_number}
                onClick={() => setWeekFilter(weekFilter === w.week_number ? null : w.week_number)}
              />
            ))}
          </Box>
        )
      )}

      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
        {filteredWeeks.map((w) => (
          <WeekDropzone
            key={w.id}
            week={w}
            chips={chipsForWeek(w.id)}
            courseSubjects={courseSubjects}
            onChipDelete={onChipDelete}
            selectedCount={selectedCount}
            onAssignSelected={onAssignSelected}
          />
        ))}
        {weeks.length === 0 && (
          <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }} sx={{ py: 2, textAlign: "center" }}>No weeks in this group</Typography>
        )}
        {weeks.length > 0 && filteredWeeks.length === 0 && (
          <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }} sx={{ py: 2, textAlign: "center" }}>No weeks match filter</Typography>
        )}
      </Box>
      {viewCourseHref && (
        <Button variant="outlined" endIcon={<OpenInNewIcon />} component={RouterLink} to={viewCourseHref}
          sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "text.primary", "&:hover": { borderColor: PURPLE, color: PURPLE, bgcolor: PURPLE_SOFT } }}>
          View course
        </Button>
      )}
      {extraFooter}
    </Paper>
  );
}

// ─── Test draggable row ─────────────────────────────────────────────────────────

function TestDraggableRow({ test, allocatedWeekNumbers = [], busy, selected = false, onToggle }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `ws-${testIdOf(test)}`,
    data: test,
    disabled: busy,
  });
  const tid = testIdOf(test);
  return (
    <Stack ref={setNodeRef} direction="row" alignItems="center" spacing={1.25}
      sx={{ px: 1.25, py: 1, mb: 0.75, borderRadius: 1.5, border: `1px solid ${selected ? PURPLE : "#eef2f7"}`, bgcolor: selected ? PURPLE_SOFT : "#fff", opacity: isDragging || busy ? 0.4 : 1, transition: "all 100ms ease", "&:hover": { bgcolor: selected ? PURPLE_SOFT : "#fafafa", borderColor: selected ? PURPLE : "#e2e8f0" } }}>
      <Checkbox size="small" checked={selected} onChange={() => onToggle && onToggle(tid)} disabled={busy}
        sx={{ p: 0.5, color: "#cbd5e1", "&.Mui-checked": { color: PURPLE } }} />
      <Avatar sx={{ width: 30, height: 30, bgcolor: "#eef2ff", color: "#4338ca", cursor: busy ? "wait" : "grab" }} {...attributes} {...listeners}>
        <QuizIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0, cursor: busy ? "wait" : "grab" }} {...attributes} {...listeners}>
        <Typography variant="body2" fontWeight={600} noWrap>{testNameOf(test)}</Typography>
        {allocatedWeekNumbers.length > 0 && (
          <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} noWrap>
            {allocatedWeekNumbers.length === 1 ? `Week ${allocatedWeekNumbers[0]}` : `Weeks ${allocatedWeekNumbers.join(", ")}`}
          </Typography>
        )}
      </Box>
      <Chip size="small" label={allocatedWeekNumbers.length > 0 ? `Allocated (${allocatedWeekNumbers.length})` : "Ready"}
        sx={{ height: 22, fontWeight: 600, bgcolor: allocatedWeekNumbers.length > 0 ? "#f1f5f9" : "#dcfce7", color: allocatedWeekNumbers.length > 0 ? "#475569" : "#15803d" }} />
    </Stack>
  );
}

// ─── Content draggable row ──────────────────────────────────────────────────────

function ContentDraggableRow({ item, selected, onToggle, allocatedWeekNumbers = [], busy }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `content-${item.media_id}`,
    data: item,
    disabled: busy,
  });
  return (
    <Stack ref={setNodeRef} direction="row" alignItems="center" spacing={1.25}
      sx={{ px: 1.25, py: 1, mb: 0.75, borderRadius: 1.5, border: `1px solid ${selected ? PURPLE : "#eef2f7"}`, bgcolor: selected ? PURPLE_SOFT : "#fff", cursor: busy ? "wait" : "default", "&:hover": { bgcolor: selected ? PURPLE_SOFT : "#fafafa" }, transition: "all 100ms ease", opacity: isDragging || busy ? 0.4 : 1 }}>
      <Checkbox size="small" checked={selected} onChange={() => onToggle(item.media_id)} disabled={busy}
        sx={{ p: 0.5, color: "#cbd5e1", "&.Mui-checked": { color: PURPLE } }} />
      <Avatar sx={{ width: 30, height: 30, bgcolor: "#f8fafc", color: "#64748b", cursor: busy ? "wait" : "grab" }} {...attributes} {...listeners}>
        {fileIcon(item.mime_type)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0, cursor: busy ? "wait" : "grab" }} {...attributes} {...listeners}>
        <Typography variant="body2" fontWeight={600} noWrap>{item.file_name}</Typography>
        {(item.topic_name || item.subtopic_name) && (
          <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} noWrap display="block">
            {item.topic_name}{item.subtopic_name ? ` / ${item.subtopic_name}` : ""}
          </Typography>
        )}
        {allocatedWeekNumbers.length > 0 && (
          <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} noWrap>
            {allocatedWeekNumbers.length === 1 ? `Week ${allocatedWeekNumbers[0]}` : `Weeks ${allocatedWeekNumbers.join(", ")}`}
          </Typography>
        )}
      </Box>
      <Chip size="small"
        label={allocatedWeekNumbers.length > 0 ? `Allocated (${allocatedWeekNumbers.length})` : formatSize(item.size)}
        sx={{ height: 22, fontWeight: 600, fontSize: 11, bgcolor: "#f1f5f9", color: allocatedWeekNumbers.length > 0 ? "#475569" : "#64748b" }} />
    </Stack>
  );
}

// ─── Week Allocation Tab ────────────────────────────────────────────────────────

function WeekAllocationTab({ selectedPlan, tests, ctas, setCtas, weeks, isMobile }) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [busyTestId, setBusyTestId] = useState(null);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [courseSubjects, setCourseSubjects] = useState([]);
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());

  const planId = selectedPlan?.id;

  useEffect(() => {
    if (!planId) { setCtas([]); setCourseSubjects([]); setSelectedTestIds(new Set()); return; }
    setCtas([]);
    setStatusFilter("all");
    setSubjectFilter("all");
    setSelectedTestIds(new Set());
    api.get(`admin/course-plans/${planId}/test-assignments`)
      .then((r) => setCtas(r.data?.data || []))
      .catch(() => setCtas([]));
    api.get(`admin/course-plans/${planId}/content/subjects`)
      .then((r) => setCourseSubjects((r.data?.data || []).map((s) => s.subject_name).filter(Boolean)))
      .catch(() => setCourseSubjects([]));
  }, [planId, setCtas]);

  const testSubjectById = useMemo(() => {
    const m = new Map();
    tests.forEach((t) => {
      const id = testIdOf(t);
      if (id && t.subject_name) m.set(id, t.subject_name);
    });
    return m;
  }, [tests]);

  const ctasByTest = useMemo(() => {
    const m = new Map();
    ctas.forEach((c) => {
      const arr = m.get(c.course_test_id) || [];
      arr.push(c);
      m.set(c.course_test_id, arr);
    });
    return m;
  }, [ctas]);

  const weekNumberById = useMemo(() => {
    const m = new Map();
    weeks.forEach((w) => m.set(Number(w.id), w.week_number));
    return m;
  }, [weeks]);

  const weekNumbersForTest = useCallback((testId) =>
    (ctasByTest.get(testId) || [])
      .map((c) => weekNumberById.get(Number(c.plan_week_id)))
      .filter((n) => n != null)
      .sort((a, b) => a - b),
    [ctasByTest, weekNumberById]
  );

  const isAllocatedInPlan = useCallback((testId) => weekNumbersForTest(testId).length > 0, [weekNumbersForTest]);

  const subjects = useMemo(() => {
    const names = new Set(courseSubjects);
    tests.forEach((t) => { if (t.subject_name) names.add(t.subject_name); });
    return Array.from(names).sort((a, b) => {
      const ai = courseSubjects.indexOf(a);
      const bi = courseSubjects.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [tests, courseSubjects]);

  const subjectOrder = useMemo(() => {
    if (courseSubjects.length) return courseSubjects;
    return subjects;
  }, [courseSubjects, subjects]);

  const visibleTests = useMemo(() => {
    const term = (search || "").toLowerCase();
    return tests.filter((t) => {
      if (term && !testNameOf(t).toLowerCase().includes(term)) return false;
      if (subjectFilter !== "all" && t.subject_name !== subjectFilter) return false;
      const inPlan = isAllocatedInPlan(testIdOf(t));
      if (statusFilter === "ready" && inPlan) return false;
      if (statusFilter === "allocated" && !inPlan) return false;
      return true;
    });
  }, [tests, search, statusFilter, subjectFilter, isAllocatedInPlan]);

  const totalAllocated = useMemo(() => new Set(ctas.map((c) => c.course_test_id)).size, [ctas]);
  const remaining = Math.max(tests.length - totalAllocated, 0);

  const handleDragEnd = (event) => {
    const overId = event.over?.id;
    const test = event.active?.data?.current;
    if (!overId || !test || !String(overId).startsWith("week-")) return;
    const planWeekId = Number(String(overId).replace("week-", ""));
    const testId = testIdOf(test);
    if (!testId) return;
    if (ctas.some((c) => c.course_test_id === testId && Number(c.plan_week_id) === planWeekId)) return;
    const tempId = `tmp-${testId}-${planWeekId}-${Date.now()}`;
    setCtas((prev) => [...prev, {
      id: tempId, course_test_id: testId, plan_week_id: planWeekId,
      test_name: testNameOf(test), subject_name: test.subject_name || testSubjectById.get(testId),
      _pending: true,
    }]);
    setBusyTestId(testId);
    api.post(`admin/assign/test/${testId}/course-assignments`, { plan_week_id: planWeekId })
      .then((r) => {
        const saved = r.data?.data;
        setCtas((prev) => prev.map((c) => c.id === tempId ? {
          id: saved?.id ?? c.id, course_test_id: testId, plan_week_id: planWeekId,
          test_name: testNameOf(test), subject_name: test.subject_name || testSubjectById.get(testId),
        } : c));
        toast.success("Allocated");
      })
      .catch(() => { toast.error("Allocation failed"); setCtas((prev) => prev.filter((c) => c.id !== tempId)); })
      .finally(() => setBusyTestId(null));
  };

  const handleChipDelete = (chip) => {
    const { ctaId, testId } = chip;
    if (!ctaId || String(ctaId).startsWith("tmp-")) { setCtas((prev) => prev.filter((c) => c.id !== ctaId)); return; }
    setBusyTestId(testId);
    const snapshot = [...ctas];
    setCtas((prev) => prev.filter((c) => c.id !== ctaId));
    api.delete(`admin/assign/test/${testId}/course-assignments/${ctaId}`)
      .then(() => toast.success("Removed"))
      .catch(() => { toast.error("Remove failed"); setCtas(snapshot); })
      .finally(() => setBusyTestId(null));
  };

  const handleAssignSelectedToWeek = useCallback((weekId) => {
    const planWeekId = Number(weekId);
    const testIds = Array.from(selectedTestIds);
    if (!testIds.length) return;
    setSelectedTestIds(new Set());
    testIds.forEach((testId) => {
      if (ctas.some((c) => c.course_test_id === testId && Number(c.plan_week_id) === planWeekId)) return;
      const test = tests.find((t) => testIdOf(t) === testId);
      const tempId = `tmp-${testId}-${planWeekId}-${Date.now()}`;
      setCtas((prev) => [...prev, {
        id: tempId, course_test_id: testId, plan_week_id: planWeekId,
        test_name: testNameOf(test || {}), subject_name: test?.subject_name || testSubjectById.get(testId),
        _pending: true,
      }]);
      api.post(`admin/assign/test/${testId}/course-assignments`, { plan_week_id: planWeekId })
        .then((r) => {
          const saved = r.data?.data;
          setCtas((prev) => prev.map((c) => c.id === tempId ? {
            id: saved?.id ?? c.id, course_test_id: testId, plan_week_id: planWeekId,
            test_name: testNameOf(test || {}), subject_name: test?.subject_name || testSubjectById.get(testId),
          } : c));
        })
        .catch(() => { setCtas((prev) => prev.filter((c) => c.id !== tempId)); });
    });
    toast.success(testIds.length > 1 ? `${testIds.length} worksheets allocated` : "Allocated");
  }, [selectedTestIds, ctas, tests, testSubjectById, setCtas]);

  const chipsForWeek = (weekId) =>
    ctas.filter((c) => Number(c.plan_week_id) === Number(weekId))
      .map((c) => ({
        id: c.id, ctaId: c.id, testId: c.course_test_id,
        label: c.test_name || `Test #${c.course_test_id}`,
        subjectName: c.subject_name || testSubjectById.get(c.course_test_id) || "Other",
      }));

  if (!selectedPlan) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <RadioButtonUncheckedIcon sx={{ fontSize: 36, color: "#e2e8f0", mb: 1 }} />
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>Select a group from the sidebar to manage test allocation</Typography>
      </Box>
    );
  }

  const weeksPanel = (
    <WeeksPanel
      weeks={weeks}
      chipsForWeek={chipsForWeek}
      courseSubjects={subjectOrder}
      onChipDelete={handleChipDelete}
      totalAllocated={totalAllocated}
      remaining={remaining}
      selectedCount={selectedTestIds.size}
      onAssignSelected={handleAssignSelectedToWeek}
    />
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #eef2f7" }}>
            <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
              <StepBadge n={3} />
              <Typography variant="subtitle1" fontWeight={700}>Allocate Worksheets (Tests) to Weeks</Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} display="block" mb={1}>
              Drag a worksheet onto a week · or select worksheets and use <strong>+ selected</strong> on a week.
            </Typography>
            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
              <TextField size="small" sx={{ flex: 1, minWidth: 160 }} placeholder="Search worksheets…" value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#374151" }} /></InputAdornment> }} />
              {subjects.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
                    <MenuItem value="all">All Subjects</MenuItem>
                    {subjects.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="allocated">Allocated</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            {selectedTestIds.size > 0 && (
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
                  {selectedTestIds.size} selected ·{" "}
                  <Box component="span" sx={{ color: PURPLE, cursor: "pointer" }} onClick={() => setSelectedTestIds(new Set())}>Clear</Box>
                </Typography>
              </Stack>
            )}
            <Box sx={{ maxHeight: { xs: 420, md: 580 }, overflow: "auto", pr: 0.5 }}>
              {visibleTests.map((t) => {
                const tid = testIdOf(t);
                return (
                  <TestDraggableRow
                    key={tid}
                    test={t}
                    allocatedWeekNumbers={weekNumbersForTest(tid)}
                    busy={busyTestId === tid}
                    selected={selectedTestIds.has(tid)}
                    onToggle={(id) => setSelectedTestIds((prev) => {
                      const next = new Set(prev);
                      next.has(id) ? next.delete(id) : next.add(id);
                      return next;
                    })}
                  />
                );
              })}
              {visibleTests.length === 0 && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>No worksheets</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        {!isMobile && <Grid item md={6}>{weeksPanel}</Grid>}
      </Grid>

      {isMobile && (
        <>
          <Fab onClick={() => setMobilePanelOpen(true)} sx={{ position: "fixed", bottom: 16, right: 16, bgcolor: PURPLE, color: "#fff", "&:hover": { bgcolor: PURPLE_DARK } }}>
            <Badge badgeContent={totalAllocated} color="success"><ViewWeekIcon /></Badge>
          </Fab>
          <Drawer anchor="bottom" open={mobilePanelOpen} onClose={() => setMobilePanelOpen(false)} PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh" } }}>
            <Box sx={{ p: 1.5 }}>{weeksPanel}</Box>
          </Drawer>
        </>
      )}
    </DndContext>
  );
}

// ─── Content Allocation Tab ─────────────────────────────────────────────────────

function ContentAllocationTab({ selectedPlan, weeks, acdemicCourseId, courseId, isMobile }) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));
  const planId = selectedPlan?.id;

  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [masterTopics, setMasterTopics] = useState([]);
  const [topicId, setTopicId] = useState("");
  const [masterSubtopics, setMasterSubtopics] = useState([]);
  const [subtopicId, setSubtopicId] = useState("");
  const [contentItems, setContentItems] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [pendingAllocations, setPendingAllocations] = useState({});
  const [selectedMediaIds, setSelectedMediaIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyMediaId, setBusyMediaId] = useState(null);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const contentUploadHref = courseId ? `/admin/course-content?courseId=${courseId}` : "/admin/course-content";

  const loadAllocations = useCallback(() => {
    if (!planId) { setAllocations([]); return; }
    api.get(`admin/course-plans/${planId}/content/allocations`)
      .then((r) => setAllocations(r.data?.data || []))
      .catch(() => setAllocations([]));
  }, [planId]);

  useEffect(() => {
    if (!planId) { setSubjects([]); setSubjectId(""); return; }
    api.get(`admin/course-plans/${planId}/content/subjects`)
      .then((r) => setSubjects(r.data?.data || []))
      .catch(() => setSubjects([]));
  }, [planId]);

  useEffect(() => {
    setSubjectId("");
    setTopicId("");
    setSubtopicId("");
    setContentItems([]);
    setPendingAllocations({});
    setSelectedMediaIds(new Set());
    loadAllocations();
  }, [planId, loadAllocations]);

  useEffect(() => {
    if (!acdemicCourseId || !subjectId) { setMasterTopics([]); setTopicId(""); return; }
    api.get(`admin/fetch/topics/by-academic-subject/${acdemicCourseId}/${subjectId}`)
      .then((r) => setMasterTopics(r.data?.data || []))
      .catch(() => setMasterTopics([]));
  }, [acdemicCourseId, subjectId]);

  useEffect(() => {
    if (!topicId) { setMasterSubtopics([]); setSubtopicId(""); return; }
    api.get(`admin/fetch/topics/subtopics/${topicId}`)
      .then((r) => setMasterSubtopics(r.data?.data || []))
      .catch(() => setMasterSubtopics([]));
  }, [topicId]);

  useEffect(() => {
    if (!planId || !subjectId) { setContentItems([]); return; }
    const params = { subject_id: subjectId };
    if (topicId) params.topic_id = topicId;
    if (subtopicId) params.subtopic_id = subtopicId;
    api.get(`admin/course-plans/${planId}/content/browse`, { params })
      .then((r) => setContentItems(r.data?.data || []))
      .catch(() => setContentItems([]));
  }, [planId, subjectId, topicId, subtopicId]);

  useEffect(() => {
    const map = {};
    allocations.forEach((a) => {
      if (!map[a.plan_week_id]) map[a.plan_week_id] = [];
      map[a.plan_week_id].push({
        media_id: a.media_id, file_name: a.file_name, url: a.url,
        mime_type: a.mime_type, id: a.id, subject_name: a.subject_name,
      });
    });
    setPendingAllocations(map);
  }, [allocations]);

  const subjectOrder = useMemo(
    () => subjects.map((s) => s.subject_name).filter(Boolean),
    [subjects]
  );

  const subjectNameByMediaId = useMemo(() => {
    const m = new Map();
    contentItems.forEach((i) => {
      if (i.media_id && i.subject_name) m.set(i.media_id, i.subject_name);
    });
    Object.values(pendingAllocations).flat().forEach((e) => {
      if (e.media_id && e.subject_name) m.set(e.media_id, e.subject_name);
    });
    return m;
  }, [contentItems, pendingAllocations]);

  const totalAllocatedCount = useMemo(() => {
    const ids = new Set();
    Object.values(pendingAllocations).flat().forEach((e) => ids.add(e.media_id));
    return ids.size;
  }, [pendingAllocations]);

  const hasPendingChanges = useMemo(() =>
    Object.values(pendingAllocations).flat().some((e) => e._temp),
    [pendingAllocations]
  );

  const visibleItems = useMemo(() => {
    const term = (search || "").toLowerCase();
    return contentItems.filter((i) => !term || (i.file_name || "").toLowerCase().includes(term) || (i.topic_name || "").toLowerCase().includes(term));
  }, [contentItems, search]);

  const weekNumberById = useMemo(() => {
    const m = new Map();
    weeks.forEach((w) => m.set(Number(w.id), w.week_number));
    return m;
  }, [weeks]);

  const weekNumbersForMedia = useCallback((mediaId) => {
    const nums = [];
    Object.entries(pendingAllocations).forEach(([weekId, items]) => {
      if (items.some((e) => e.media_id === mediaId)) {
        const n = weekNumberById.get(Number(weekId));
        if (n != null) nums.push(n);
      }
    });
    return nums.sort((a, b) => a - b);
  }, [pendingAllocations, weekNumberById]);

  const allocateToWeek = useCallback(async (planWeekId, mediaIds) => {
    const ids = mediaIds.filter(
      (mediaId) => !(pendingAllocations[planWeekId] || []).some((e) => e.media_id === mediaId)
    );
    if (!ids.length || !planId) return;

    const itemMap = {};
    contentItems.forEach((i) => { itemMap[i.media_id] = i; });

    setBusyMediaId(ids.length === 1 ? ids[0] : null);
    try {
      await api.post(`admin/course-plans/${planId}/content/allocate`, {
        plan_week_id: planWeekId,
        media_ids: ids,
      });
      toast.success(ids.length > 1 ? `${ids.length} files allocated` : "Allocated");
      loadAllocations();
    } catch {
      toast.error("Allocation failed");
      loadAllocations();
    } finally {
      setBusyMediaId(null);
    }
  }, [planId, pendingAllocations, contentItems, loadAllocations]);

  const handleDragEnd = (event) => {
    const overId = event.over?.id;
    const item = event.active?.data?.current;
    if (!overId || !item || !String(overId).startsWith("week-")) return;
    const planWeekId = Number(String(overId).replace("week-", ""));
    const mediaId = item.media_id;
    if (!mediaId) return;
    allocateToWeek(planWeekId, [mediaId]);
  };

  const handleAssignSelectedToWeek = (weekId) => {
    const ids = Array.from(selectedMediaIds);
    if (!ids.length) return;
    allocateToWeek(Number(weekId), ids);
    setSelectedMediaIds(new Set());
  };

  const handleChipDelete = (chip) => {
    const { weekId, mediaId, allocationId } = chip;
    setPendingAllocations((prev) => ({ ...prev, [weekId]: (prev[weekId] || []).filter((e) => e.media_id !== mediaId) }));
    if (allocationId && !String(allocationId).startsWith("tmp-")) {
      api.delete(`admin/course-plans/${planId}/content/weeks/${weekId}/media/${mediaId}`)
        .then(() => toast.success("Removed"))
        .catch(() => { toast.error("Remove failed"); loadAllocations(); });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [weekIdStr, items] of Object.entries(pendingAllocations)) {
        const tempItems = items.filter((i) => i._temp);
        if (tempItems.length > 0) {
          await api.post(`admin/course-plans/${planId}/content/allocate`, {
            plan_week_id: Number(weekIdStr),
            media_ids: tempItems.map((i) => i.media_id),
          });
        }
      }
      toast.success("Allocation saved");
      loadAllocations();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const chipsForWeek = (weekId) =>
    (pendingAllocations[Number(weekId)] || []).map((e) => ({
      id: e.id, weekId: Number(weekId), mediaId: e.media_id, allocationId: e.id,
      label: e.file_name || `Media #${e.media_id}`,
      subjectName: e.subject_name || subjectNameByMediaId.get(e.media_id) || "Other",
    }));

  if (!selectedPlan) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <RadioButtonUncheckedIcon sx={{ fontSize: 36, color: "#e2e8f0", mb: 1 }} />
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>Select a group from the sidebar to manage content allocation</Typography>
      </Box>
    );
  }

  const saveFooter = (
    <Stack direction="row" spacing={1} mt={1.5}>
      <Button variant="contained"
        startIcon={saving ? <CircularProgress size={16} sx={{ color: "white" }} /> : <SaveIcon />}
        onClick={handleSave} disabled={saving || !hasPendingChanges}
        sx={{ flex: 1, textTransform: "none", fontWeight: 600, bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK }, "&:disabled": { opacity: 0.5 } }}>
        {saving ? "Saving…" : "Save Allocation"}
      </Button>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAllocations} disabled={saving}
        sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "text.primary", "&:hover": { borderColor: PURPLE, color: PURPLE, bgcolor: PURPLE_SOFT } }}>
        Reset
      </Button>
    </Stack>
  );

  const weeksPanel = (
    <WeeksPanel
      weeks={weeks}
      chipsForWeek={chipsForWeek}
      courseSubjects={subjectOrder}
      onChipDelete={handleChipDelete}
      totalAllocated={totalAllocatedCount}
      remaining={0}
      selectedCount={selectedMediaIds.size}
      onAssignSelected={handleAssignSelectedToWeek}
      extraFooter={hasPendingChanges ? saveFooter : (
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAllocations} disabled={saving}
          sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "text.primary", "&:hover": { borderColor: PURPLE, color: PURPLE, bgcolor: PURPLE_SOFT } }}>
          Refresh
        </Button>
      )}
    />
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #eef2f7" }}>
            <Stack direction="row" spacing={1.25} alignItems="center" mb={0.5}>
              <StepBadge n={3} />
              <Typography variant="subtitle1" fontWeight={700}>Allocate Content to Weeks</Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} display="block" mb={1.5}>
              Drag content onto a week in the allocation panel. Saves on drop. Or select files and use + selected on a week.
            </Typography>

            <Grid container spacing={1} mb={1.5}>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <Select value={subjectId} onChange={(e) => { setSubjectId(e.target.value); setTopicId(""); setSubtopicId(""); }} displayEmpty>
                    <MenuItem value="">Subject</MenuItem>
                    {subjects.map((s) => <MenuItem key={s.subject_id} value={s.subject_id}>{s.subject_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <Select value={topicId} onChange={(e) => { setTopicId(e.target.value); setSubtopicId(""); }} displayEmpty disabled={!subjectId}>
                    <MenuItem value="">Topic</MenuItem>
                    {masterTopics.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <Select value={subtopicId} onChange={(e) => setSubtopicId(e.target.value)} displayEmpty disabled={!topicId}>
                    <MenuItem value="">Sub Topic</MenuItem>
                    {masterSubtopics.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {subjectId ? (
              <>
                <Stack direction="row" spacing={1} mb={1} alignItems="center">
                  <TextField size="small" sx={{ flex: 1 }} placeholder="Search content…" value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#374151" }} /></InputAdornment> }} />
                  {selectedMediaIds.size > 0 && (
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} sx={{ whiteSpace: "nowrap" }}>
                      {selectedMediaIds.size} selected ·{" "}
                      <Box component="span" sx={{ color: PURPLE, cursor: "pointer" }} onClick={() => setSelectedMediaIds(new Set())}>Clear</Box>
                    </Typography>
                  )}
                </Stack>
                <Box sx={{ maxHeight: { xs: 360, md: 480 }, overflow: "auto", pr: 0.5 }}>
                  {visibleItems.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }} mb={contentItems.length === 0 ? 1.5 : 0}>
                        {contentItems.length === 0 ? "No content uploaded for this course yet." : "No matches"}
                      </Typography>
                      {contentItems.length === 0 && (
                        <Button variant="contained" component={RouterLink} to={contentUploadHref}
                          sx={{ textTransform: "none", fontWeight: 600, bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}>
                          Add Content
                        </Button>
                      )}
                    </Box>
                  ) : (
                    visibleItems.map((item) => (
                      <ContentDraggableRow
                        key={item.media_id}
                        item={item}
                        selected={selectedMediaIds.has(item.media_id)}
                        allocatedWeekNumbers={weekNumbersForMedia(item.media_id)}
                        busy={busyMediaId === item.media_id}
                        onToggle={(id) => setSelectedMediaIds((prev) => {
                          const next = new Set(prev);
                          next.has(id) ? next.delete(id) : next.add(id);
                          return next;
                        })}
                      />
                    ))
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <FolderIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>Select a subject to browse content</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {!isMobile && <Grid item md={6}>{weeksPanel}</Grid>}
      </Grid>

      {isMobile && (
        <>
          <Fab onClick={() => setMobilePanelOpen(true)} sx={{ position: "fixed", bottom: 16, right: 16, bgcolor: PURPLE, color: "#fff", "&:hover": { bgcolor: PURPLE_DARK } }}>
            <Badge badgeContent={totalAllocatedCount} color="success"><ViewWeekIcon /></Badge>
          </Fab>
          <Drawer anchor="bottom" open={mobilePanelOpen} onClose={() => setMobilePanelOpen(false)} PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh" } }}>
            <Box sx={{ p: 1.5 }}>{weeksPanel}</Box>
          </Drawer>
        </>
      )}
    </DndContext>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function ResourceAllocation() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [tabIndex, setTabIndex] = useState(0);
  const [ctas, setCtas] = useState([]);
  const {
    academicCourses,
    acdemicCourseId,
    setAcdemicCourseId,
    plans,
    planId,
    setPlanId,
    tests,
    selectedPlan,
    weeks,
    catalogCourseId,
    academicCourseLabel,
    viewCourseHref,
  } = useAcademicCoursePlans({ loadTests: true });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        Dashboard / <Box component="span" sx={{ color: PURPLE, fontWeight: 600 }}>Resource Allocation</Box>
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5} mb={0.5}>Resource Allocation</Typography>
      <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }} mb={2}>
        Select a course and group, then allocate worksheets or content files to plan weeks.
      </Typography>

      <CourseHeader
        academicCourses={academicCourses}
        acdemicCourseId={acdemicCourseId}
        setAcdemicCourseId={setAcdemicCourseId}
        plans={plans}
        worksheetCount={tests.length}
        worksheetLabel={tabIndex === 0 ? "Worksheets" : tabIndex === 1 ? "Content Items" : "Plan Weeks"}
        viewCourseHref={viewCourseHref}
      />

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <GroupsSidebar
            plans={plans}
            planId={planId}
            setPlanId={setPlanId}
            academicCourseLabel={academicCourseLabel}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #eef2f7", overflow: "hidden" }}>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              sx={{
                borderBottom: "1px solid #eef2f7",
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
                "& .Mui-selected": { color: PURPLE },
                "& .MuiTabs-indicator": { bgcolor: PURPLE },
              }}
            >
              <Tab icon={<QuizIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Worksheet Allocation" />
              <Tab icon={<FolderIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Content Allocation" />
              <Tab icon={<EventIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Deadline Management" />
            </Tabs>
            <Box sx={{ p: 2 }}>
              {tabIndex === 0 && (
                <WeekAllocationTab
                  selectedPlan={selectedPlan}
                  tests={tests}
                  ctas={ctas}
                  setCtas={setCtas}
                  weeks={weeks}
                  isMobile={isMobile}
                />
              )}
              {tabIndex === 1 && (
                <ContentAllocationTab
                  selectedPlan={selectedPlan}
                  weeks={weeks}
                  acdemicCourseId={acdemicCourseId}
                  courseId={catalogCourseId}
                  isMobile={isMobile}
                />
              )}
              {tabIndex === 2 && (
                <DeadlineManagementTab
                  selectedPlan={selectedPlan}
                  planId={planId}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
